import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { toastify } from '../../../../utils/toast';
import { makeRequest2 } from '../../../../utils/makeRequest';
import {
  add_delivery_time_mark, get_delivery_time_marks, delete_delivery_time_mark, edit_delivery_time_mark,
  add_payment_term_mark, get_payment_term_marks, delete_payment_term_mark, edit_payment_term_mark
} from '../../../../utils/urls';
import { useFacilityStore } from '../../../../app/z-store/store';

const RFQGradingTab = () => {
  const facilityId = useFacilityStore((state) => state.facilityId);

  // State for delivery time marks
  const [deliveryTimeMarks, setDeliveryTimeMarks] = useState([]);
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  const [currentDeliveryMark, setCurrentDeliveryMark] = useState({ deliveryDays: '', marks: '' });
  const [editingDeliveryId, setEditingDeliveryId] = useState(null);

  // State for payment term marks
  const [paymentTermMarks, setPaymentTermMarks] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [currentPaymentMark, setCurrentPaymentMark] = useState({ paymentTerm: '', marks: '' });
  const [editingPaymentId, setEditingPaymentId] = useState(null);

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (facilityId) {
      fetchGradingData();
    }
  }, [facilityId]);

  const fetchGradingData = async () => {
    setIsLoading(true);
    try {
      // Fetch delivery time marks
      const deliveryResponse = await makeRequest2(
        `${get_delivery_time_marks}/${facilityId}`,
        'GET'
      );
      if (deliveryResponse.success) {
        // Extract the actual array from the nested structure
        setDeliveryTimeMarks(deliveryResponse.data.data?.deliveryTimeMarks || []);
      }

      // Fetch payment term marks
      const paymentResponse = await makeRequest2(
        `${get_payment_term_marks}/${facilityId}`,
        'GET'
      );
      if (paymentResponse.success) {
        // Extract the actual array from the nested structure
        setPaymentTermMarks(paymentResponse.data.data?.paymentTermMarks || []);
      }
    } catch (error) {
      console.error('Error fetching grading data:', error);
      toastify('Error loading grading data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Delivery Time Mark Functions
  const handleAddDeliveryMark = () => {
    setCurrentDeliveryMark({ deliveryDays: '', marks: '' });
    setEditingDeliveryId(null);
    setShowDeliveryDialog(true);
  };

  const handleEditDeliveryMark = (mark) => {
    setCurrentDeliveryMark({
      deliveryDays: mark.deliveryDays,
      marks: mark.marks
    });
    setEditingDeliveryId(mark._id);
    setShowDeliveryDialog(true);
  };

  const handleSaveDeliveryMark = async () => {
    if (!currentDeliveryMark.deliveryDays || !currentDeliveryMark.marks) {
      toastify('Please fill in all fields', 'error');
      return;
    }

    if (currentDeliveryMark.deliveryDays <= 0 || currentDeliveryMark.marks <= 0) {
      toastify('Delivery days and marks must be positive numbers', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        facilityId,
        deliveryDays: currentDeliveryMark.deliveryDays,
        marks: currentDeliveryMark.marks
      };

      const url = editingDeliveryId
        ? `${edit_delivery_time_mark}/${facilityId}/${editingDeliveryId}`
        : `${add_delivery_time_mark}/${facilityId}`;
      const method = editingDeliveryId ? 'PUT' : 'POST';

      const response = await makeRequest2(url, method, payload);

      if (response.success) {
        toastify(
          editingDeliveryId ? 'Delivery time mark updated successfully' : 'Delivery time mark added successfully',
          'success'
        );
        setShowDeliveryDialog(false);
        await fetchGradingData();
      } else {
        toastify(response.error || 'Error saving delivery time mark', 'error');
      }
    } catch (error) {
      console.error('Error saving delivery time mark:', error);
      toastify('Error saving delivery time mark', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDeliveryMark = (markId) => {
    confirmDialog({
      message: 'Are you sure you want to delete this delivery time mark?',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        setIsSubmitting(true);
        try {
          const response = await makeRequest2(
            `${delete_delivery_time_mark}/${facilityId}/${markId}`,
            'DELETE'
          );

          if (response.success) {
            toastify('Delivery time mark deleted successfully', 'success');
            await fetchGradingData();
          } else {
            toastify(response.error || 'Error deleting delivery time mark', 'error');
          }
        } catch (error) {
          console.error('Error deleting delivery time mark:', error);
          toastify('Error deleting delivery time mark', 'error');
        } finally {
          setIsSubmitting(false);
        }
      }
    });
  };

  // Payment Term Mark Functions
  const handleAddPaymentMark = () => {
    setCurrentPaymentMark({ paymentTerm: '', marks: '' });
    setEditingPaymentId(null);
    setShowPaymentDialog(true);
  };

  const handleEditPaymentMark = (mark) => {
    setCurrentPaymentMark({
      paymentTerm: mark.paymentTerm,
      marks: mark.marks
    });
    setEditingPaymentId(mark._id);
    setShowPaymentDialog(true);
  };

  const handleSavePaymentMark = async () => {
    if (!currentPaymentMark.paymentTerm || !currentPaymentMark.marks) {
      toastify('Please fill in all fields', 'error');
      return;
    }

    if (currentPaymentMark.marks <= 0) {
      toastify('Marks must be a positive number', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        facilityId,
        paymentTerm: currentPaymentMark.paymentTerm.trim(),
        marks: currentPaymentMark.marks
      };

      const url = editingPaymentId
        ? `${edit_payment_term_mark}/${facilityId}/${editingPaymentId}`
        : `${add_payment_term_mark}/${facilityId}`;
      const method = editingPaymentId ? 'PUT' : 'POST';

      const response = await makeRequest2(url, method, payload);

      if (response.success) {
        toastify(
          editingPaymentId ? 'Payment term mark updated successfully' : 'Payment term mark added successfully',
          'success'
        );
        setShowPaymentDialog(false);
        await fetchGradingData();
      } else {
        toastify(response.error || 'Error saving payment term mark', 'error');
      }
    } catch (error) {
      console.error('Error saving payment term mark:', error);
      toastify('Error saving payment term mark', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePaymentMark = (markId) => {
    confirmDialog({
      message: 'Are you sure you want to delete this payment term mark?',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        setIsSubmitting(true);
        try {
          const response = await makeRequest2(
            `${delete_payment_term_mark}/${facilityId}/${markId}`,
            'DELETE'
          );

          if (response.success) {
            toastify('Payment term mark deleted successfully', 'success');
            await fetchGradingData();
          } else {
            toastify(response.error || 'Error deleting payment term mark', 'error');
          }
        } catch (error) {
          console.error('Error deleting payment term mark:', error);
          toastify('Error deleting payment term mark', 'error');
        } finally {
          setIsSubmitting(false);
        }
      }
    });
  };

  // Action body templates for DataTable
  const deliveryActionBodyTemplate = (rowData) => {
    return (
      <div className="d-flex gap-2">
        <Button
          icon="ti ti-edit"
          className="p-button-rounded p-button-success p-ml-2"
          onClick={() => handleEditDeliveryMark(rowData)}
          disabled={isSubmitting}
          tooltip="Edit"
        />
        <Button
          icon="ti ti-trash"
          className="p-button-rounded p-button-warning"
          onClick={() => handleDeleteDeliveryMark(rowData._id)}
          disabled={isSubmitting}
          tooltip="Delete"
        />
      </div>
    );
  };

  const paymentActionBodyTemplate = (rowData) => {
    return (
      <div className="d-flex gap-2">
        <Button
          icon="ti ti-edit"
          className="p-button-rounded p-button-success p-ml-2"
          onClick={() => handleEditPaymentMark(rowData)}
          disabled={isSubmitting}
          tooltip="Edit"
        />
        <Button
          icon="ti ti-trash"
          className="p-button-rounded p-button-warning"
          onClick={() => handleDeletePaymentMark(rowData._id)}
          disabled={isSubmitting}
          tooltip="Delete"
        />

      </div>
    );
  };

  // Dialog footers
  const deliveryDialogFooter = (
    <div className="d-flex justify-content-end gap-2">
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setShowDeliveryDialog(false)}
        disabled={isSubmitting}
      />
      <Button
        label={editingDeliveryId ? 'Update' : 'Save'}
        icon="pi pi-check"
        onClick={handleSaveDeliveryMark}
        disabled={isSubmitting}
      />
    </div>
  );

  const paymentDialogFooter = (
    <div className="d-flex justify-content-end gap-2">
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setShowPaymentDialog(false)}
        disabled={isSubmitting}
      />
      <Button
        label={editingPaymentId ? 'Update' : 'Save'}
        icon="pi pi-check"
        onClick={handleSavePaymentMark}
        disabled={isSubmitting}
      />
    </div>
  );

  if (!facilityId) {
    return (
      <div className="text-center p-5">
        <div className="alert alert-warning">
          Please select a facility first.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading grading data...</p>
      </div>
    );
  }

  return (
    <div className="card">
      <ConfirmDialog />

      <div className="card-body">
        <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
          {/* Delivery Time Marks Tab */}
          <TabPanel header="Delivery Time Marks" leftIcon="pi pi-clock mr-2">
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  label="Add Delivery Mark"
                  className="btn btn-primary"
                  onClick={handleAddDeliveryMark}
                  disabled={isSubmitting}
                />
              </div>

              <DataTable
                value={deliveryTimeMarks}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25]}
                emptyMessage="No delivery time marks configured."
                sortField="deliveryDays"
                sortOrder={1}
                responsiveLayout="scroll"
              >
                <Column
                  field="deliveryDays"
                  header="Delivery Days"
                  sortable
                  body={(rowData) => `${rowData.deliveryDays} days`}
                />
                <Column
                  field="marks"
                  header="Marks"
                  sortable
                  body={(rowData) => `${rowData.marks} points`}
                />
                <Column
                  field="createdAt"
                  header="Created"
                  sortable
                  body={(rowData) => new Date(rowData.createdAt).toLocaleDateString()}
                />
                <Column
                  body={deliveryActionBodyTemplate}
                  header="Actions"
                  style={{ width: '120px', textAlign: 'center' }}
                />
              </DataTable>
            </div>
          </TabPanel>

          {/* Payment Term Marks Tab */}
          <TabPanel header="Payment Term Marks" leftIcon="pi pi-dollar mr-2">
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  label="Add Payment Mark"
                  className="btn btn-primary"
                  onClick={handleAddPaymentMark}
                  disabled={isSubmitting}
                />
              </div>

              <DataTable
                value={paymentTermMarks}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25]}
                emptyMessage="No payment term marks configured."
                sortField="paymentTerm"
                sortOrder={1}
                responsiveLayout="scroll"
              >
                <Column
                  field="paymentTerm"
                  header="Payment Term"
                  sortable
                />
                <Column
                  field="marks"
                  header="Marks"
                  sortable
                  body={(rowData) => `${rowData.marks} points`}
                />
                <Column
                  field="createdAt"
                  header="Created"
                  sortable
                  body={(rowData) => new Date(rowData.createdAt).toLocaleDateString()}
                />
                <Column
                  body={paymentActionBodyTemplate}
                  header="Actions"
                  style={{ width: '120px', textAlign: 'center' }}
                />
              </DataTable>
            </div>
          </TabPanel>
        </TabView>
      </div>

      {/* Delivery Time Mark Dialog */}
      <Dialog
        header={editingDeliveryId ? 'Edit Delivery Time Mark' : 'Add Delivery Time Mark'}
        visible={showDeliveryDialog}
        style={{ width: '450px' }}
        footer={deliveryDialogFooter}
        onHide={() => setShowDeliveryDialog(false)}
        modal
      >
        <div className="p-fluid">
          <div className="p-field mb-3">
            <label htmlFor="deliveryDays" className="form-label">
              Delivery Days <span className="text-danger">*</span>
            </label>
            <InputNumber
              id="deliveryDays"
              value={currentDeliveryMark.deliveryDays}
              onValueChange={(e) => setCurrentDeliveryMark({
                ...currentDeliveryMark,
                deliveryDays: e.value
              })}
              min={1}
              placeholder="Enter delivery days"
              className="form-control"
              style={{ width: '100%' }}
              inputStyle={{ width: '100%' }}
            />
            <small className="text-muted">Number of days for delivery</small>
          </div>

          <div className="p-field mb-3">
            <label htmlFor="deliveryMarks" className="form-label">
              Marks <span className="text-danger">*</span>
            </label>
            <InputNumber
              id="deliveryMarks"
              value={currentDeliveryMark.marks}
              onValueChange={(e) => setCurrentDeliveryMark({
                ...currentDeliveryMark,
                marks: e.value
              })}
              min={1}
              placeholder="Enter marks"
              className="form-control"
              style={{ width: '100%' }}
              inputStyle={{ width: '100%' }}
            />
            <small className="text-muted">Points awarded for this delivery timeframe</small>
          </div>
        </div>
      </Dialog>

      {/* Payment Term Mark Dialog */}
      <Dialog
        header={editingPaymentId ? 'Edit Payment Term Mark' : 'Add Payment Term Mark'}
        visible={showPaymentDialog}
        style={{ width: '450px' }}
        footer={paymentDialogFooter}
        onHide={() => setShowPaymentDialog(false)}
        modal
      >
        <div className="p-fluid">
          <div className="p-field mb-3">
            <label htmlFor="paymentTerm" className="form-label">
              Payment Term <span className="text-danger">*</span>
            </label>
            <InputText
              id="paymentTerm"
              value={currentPaymentMark.paymentTerm}
              onChange={(e) => setCurrentPaymentMark({
                ...currentPaymentMark,
                paymentTerm: e.target.value
              })}
              placeholder="e.g., Net 30, Cash on Delivery, etc."
              className="form-control"
            />
            <small className="text-muted">Description of the payment term</small>
          </div>

          <div className="p-field mb-3">
            <label htmlFor="paymentMarks" className="form-label">
              Marks <span className="text-danger">*</span>
            </label>
            <InputNumber
              id="paymentMarks"
              value={currentPaymentMark.marks}
              onValueChange={(e) => setCurrentPaymentMark({
                ...currentPaymentMark,
                marks: e.value
              })}
              min={1}
              placeholder="Enter marks"
              className="form-control"
              style={{ width: '100%' }}
              inputStyle={{ width: '100%' }}
            />
            <small className="text-muted">Points awarded for this payment term</small>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default RFQGradingTab;